import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import type { SaveResponseDTO } from '@shared/index.js';
import toast from 'react-hot-toast';
import RomManager from '../components/RomManager';
import SaveManager from '../components/SaveManager';
import '../styles/dashboard.css';

type TabType = 'library' | 'rom-storage' | 'cloud-saves';

export default function Dashboard() {
  const [saves, setSaves] = useState<SaveResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'size'>('recent');
  const [activeTab, setActiveTab] = useState<TabType>('library');
  const navigate = useNavigate();

  useEffect(() => {
    loadSaves();
  }, []);

  const loadSaves = async () => {
    try {
      const data = await apiService.getSaves();
      setSaves(data.saves);
    } catch (error) {
      console.error('Failed to load saves:', error);
      toast.error('Failed to load saves');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadROM = () => {
    navigate('/emulator-desmond');
  };

  const handlePlayGame = (save: SaveResponseDTO) => {
    navigate(`/emulator-desmond?romHash=${save.romHash}&slot=${save.saveSlot}`);
  };

  const handleDeleteSave = async (saveId: number) => {
    if (!confirm('Are you sure you want to delete this save?')) {
      return;
    }

    try {
      await apiService.deleteSave(saveId);
      toast.success('Save deleted successfully');
      loadSaves();
    } catch (error) {
      console.error('Failed to delete save:', error);
      toast.error('Failed to delete save');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    }).format(date);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getGameInitials = (name: string | null) => {
    if (!name) return 'DS';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Filter and sort saves
  const filteredAndSortedSaves = useMemo(() => {
    let filtered = saves.filter(save => {
      const gameName = (save.romName || 'Unknown Game').toLowerCase();
      const query = searchQuery.toLowerCase();
      return gameName.includes(query) || save.romHash.toLowerCase().includes(query);
    });

    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'name':
        filtered.sort((a, b) => (a.romName || 'Unknown').localeCompare(b.romName || 'Unknown'));
        break;
      case 'size':
        filtered.sort((a, b) => b.fileSize - a.fileSize);
        break;
    }

    return filtered;
  }, [saves, searchQuery, sortBy]);

  // Calculate stats
  const totalGames = saves.length;
  const totalSize = saves.reduce((acc, save) => acc + save.fileSize, 0);
  const mostRecentSave = saves.length > 0
    ? saves.reduce((latest, save) =>
        new Date(save.updatedAt) > new Date(latest.updatedAt) ? save : latest
      )
    : null;

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-content">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton skeleton-button"></div>
          </div>
        </div>
        <div className="stats-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="stat-card skeleton-card">
              <div className="skeleton skeleton-stat"></div>
            </div>
          ))}
        </div>
        <div className="saves-grid">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="save-card skeleton-card">
              <div className="skeleton skeleton-card-content"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Dashboard</h1>
            <p className="header-subtitle">
              Manage your ROMs, saves, and settings
            </p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={handleUploadROM}
              aria-label="Upload a new ROM file to play"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
              </svg>
              Play Game
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-nav">
        <button
          className={`tab-button ${activeTab === 'library' ? 'active' : ''}`}
          onClick={() => setActiveTab('library')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
          Library
        </button>
        <button
          className={`tab-button ${activeTab === 'rom-storage' ? 'active' : ''}`}
          onClick={() => setActiveTab('rom-storage')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          ROM Storage
        </button>
        <button
          className={`tab-button ${activeTab === 'cloud-saves' ? 'active' : ''}`}
          onClick={() => setActiveTab('cloud-saves')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
          </svg>
          Cloud Saves
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'library' && (
        <>
          {saves.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
          </div>
          <h2>Start Your Gaming Journey</h2>
          <p>Upload a Nintendo DS ROM file to begin playing your favorite games with cloud save sync.</p>
          <button className="btn btn-primary btn-large" onClick={handleUploadROM}>
            Upload Your First ROM
          </button>
          <p className="legal-notice">
            You must legally own any ROM files you upload. This emulator does not provide copyrighted content.
          </p>
        </div>
      ) : (
        <>
          {/* Stats Section */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon stat-icon-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">{totalGames}</div>
                <div className="stat-label">{totalGames === 1 ? 'Game' : 'Games'}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-success">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                  <line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">{formatFileSize(totalSize)}</div>
                <div className="stat-label">Total Storage</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">
                  {mostRecentSave ? formatDate(mostRecentSave.updatedAt) : 'Never'}
                </div>
                <div className="stat-label">Last Played</div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="controls-bar">
            <div className="search-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                aria-label="Search for games"
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>

            <div className="sort-options">
              <label htmlFor="sort-select" className="sort-label">Sort by:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'recent' | 'name' | 'size')}
                className="sort-select"
                aria-label="Sort games by"
              >
                <option value="recent">Recently Played</option>
                <option value="name">Name</option>
                <option value="size">Size</option>
              </select>
            </div>
          </div>

          {/* Games Grid */}
          {filteredAndSortedSaves.length === 0 ? (
            <div className="no-results">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <h3>No games found</h3>
              <p>Try adjusting your search query</p>
              <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
                Clear Search
              </button>
            </div>
          ) : (
            <div className="saves-grid">
              {filteredAndSortedSaves.map((save) => (
                <div key={save.id} className="save-card">
                  <div className="save-thumbnail">
                    <div className="game-icon">{getGameInitials(save.romName)}</div>
                  </div>
                  <div className="save-info">
                    <h3 className="game-title">{save.romName || 'Unknown Game'}</h3>
                    <div className="save-meta-row">
                      <span className="meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        </svg>
                        {formatFileSize(save.fileSize)}
                      </span>
                      <span className="meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {formatDate(save.updatedAt)}
                      </span>
                    </div>
                    <div className="save-slot-badge">Slot {save.saveSlot}</div>
                  </div>
                  <div className="save-actions">
                    <button
                      className="btn btn-primary btn-play"
                      onClick={() => handlePlayGame(save)}
                      aria-label={`Continue playing ${save.romName || 'Unknown Game'}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                      Play
                    </button>
                    <button
                      className="btn btn-ghost btn-icon"
                      onClick={() => handleDeleteSave(save.id)}
                      aria-label={`Delete save for ${save.romName || 'Unknown Game'}`}
                      title="Delete save"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* ROM Storage Tab */}
      {activeTab === 'rom-storage' && (
        <div className="tab-content">
          <RomManager />
        </div>
      )}

      {/* Cloud Saves Tab */}
      {activeTab === 'cloud-saves' && (
        <div className="tab-content">
          <SaveManager />
        </div>
      )}

    </div>
  );
}
